import {useQuery} from "@apollo/client";
import moment from "moment-timezone";
import {plural} from "pluralize";
import {useEffect, useMemo, useState} from "react";
import {useCookies} from "react-cookie";
import {useNavigate} from "react-router";
import {useDebounce} from "use-debounce";

import {useCheckoutContext, useCurrentUserContext} from "@/components/context/hooks.ts";
import {
  CalculatedBillingDataResponse,
  GQL_CALCULATE_BILLING_DATA,
  GQL_PLAN_INFO,
  PlanInfoResult,
} from "@/lib/queries/billing.ts";
import {AccountType, PlanLimits} from "@/lib/types/accounts.ts";
import {AddedValue, CalculatedBillingData, Plan} from "@/lib/types/billing.ts";
import {AddonSection} from "@/pages/billing/manage-addons/components/AddonSection.tsx";
import {AddonsSummary} from "@/pages/billing/manage-addons/components/AddonsSummary.tsx";
import {LimitsWarningBanner} from "@/pages/billing/manage-addons/components/LimitsWarningBanner.tsx";
import {ADDON_SECTIONS, PURCHASE_ITEM_NAMES} from "@/pages/billing/utils.ts";

const MAX_ADDED_VALUES_COST = 1000.0;

export const ManageAddons = () => {
  const {user} = useCurrentUserContext();
  const [addedValues, setAddedValues] = useState<Record<string, number>>({});
  const {data: planData} = useQuery<PlanInfoResult>(GQL_PLAN_INFO, {
    variables: {
      plan: user?.account.plan,
      ignoreAccountPlan: false,
    },
  });
  const {hasActiveSubscription, isSubscriptionPastDue} = useCheckoutContext();
  const navigateFn = useNavigate();
  const addedItems = useMemo(
    () =>
      Object.entries<number>(addedValues).map(([k, v]) => ({
        limit_field: k,
        amount: v,
      })),
    [addedValues]
  ) as AddedValue[];
  const [addedItemsDebounced] = useDebounce(addedItems, 300);
  const {data: calculatedData} = useQuery<CalculatedBillingDataResponse>(GQL_CALCULATE_BILLING_DATA, {
    variables: {
      billingPeriod: user?.account.billing_period,
      addedItems: addedItemsDebounced,
      skipApplicabilityCheck: true,
    },
  });
  const [calcDataLoaded, setCalcDataLoaded] = useState(false);
  const calculatedBillingData = calculatedData?.calculateBillingData as CalculatedBillingData;
  const unitsPrices = calculatedBillingData?.units_prices || [];
  const addedCost = calculatedBillingData?.added_cost || 0;
  const addedCostFull = calculatedBillingData?.added_cost_full || 0;
  const basePrice = calculatedBillingData?.base_price || 0;
  const [cookies, setCookie] = useCookies(["subscriptionPageNextGen"]);

  useEffect(() => {
    const savedData = cookies.subscriptionPageNextGen;
    if (savedData) setAddedValues(savedData);
    // eslint-disable-next-line
  }, [calcDataLoaded]);
  // When it comes to units prices, we don't need to refresh them every time
  // It is desirable to memoize them once they are loaded, so we can avoid
  // flickering when the user changes the values
  // eslint-disable-next-line
  const unitsPricesMemo = useMemo(() => unitsPrices, [calcDataLoaded]);
  useEffect(() => {
    if (unitsPrices && unitsPrices.length > 0) {
      setCalcDataLoaded(true);
    }
  }, [unitsPrices]);
  return (
    <>
      <LimitsWarningBanner />
      {planData &&
        ADDON_SECTIONS.map((section, index) => (
          <AddonSection
            key={index}
            {...section}
            limits={planData.planInfo.limits}
            addedValues={addedValues}
            unitsPrices={unitsPricesMemo}
            currentPlan={user?.account.plan as Plan}
            onChange={(field, value) => {
              const newAddedValues = {...addedValues, [field]: value};
              setAddedValues(newAddedValues);
              setCookie("subscriptionPageNextGen", newAddedValues, {expires: moment().add(1, "days").toDate()});
            }}
          />
        ))}
      {Math.max(...Object.values(addedValues)) > 0 &&
        calcDataLoaded &&
        !!user?.account &&
        hasActiveSubscription !== undefined && (
          <>
            <AddonsSummary
              basePrice={basePrice}
              addedCost={addedCost}
              addedCostFull={addedCostFull}
              userCanPurchase={user?.has_purchase_permission === true}
              accountCanPurchase={user?.account.type === AccountType.REGULAR && hasActiveSubscription}
              isInvoiced={user?.account.type === AccountType.INVOICED}
              isPastDue={isSubscriptionPastDue || false}
              isMaxHit={addedCost > MAX_ADDED_VALUES_COST}
              onPurchase={() => {
                navigateFn("/billing/checkout", {
                  state: {
                    addedValues: addedItems,
                    plan: user?.account.plan,
                    billingPeriod: user?.account.billing_period,
                  },
                });
              }}
              onQuote={() => {
                const addedItemsRequest = addedItems
                  .filter((item) => item.amount > 0)
                  .map((item) => `${plural(PURCHASE_ITEM_NAMES[item.limit_field as keyof PlanLimits])}: ${item.amount}`)
                  .join("\n");
                navigateFn("/billing/custom-quote", {
                  state: {
                    custom_monitoring_needs: addedItemsRequest,
                  },
                });
              }}
            />
          </>
        )}
      <div style={{height: "6rem"}}></div>
    </>
  );
};
